/**
 * Home: today's practices (one tap = done), the next step on each branch
 * being worked on, and what was learned lately. No graph here on purpose.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Api, api } from '@/core/api';
import { fa, formatJalali } from '@/core/jalali';
import { SETTING_FA, STATUS_FA, type PlanItem } from '@/core/types';
import { useProfile } from '@/app/ProfileShell';

export function TodayPage() {
  const { id } = useParams({ from: '/authed/p/$id' });
  const profile = useProfile();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['today', id], queryFn: () => Api.today(id) });
  const [toast, setToast] = useState<string | null>(null);
  const baseline = useQuery({
    queryKey: ['baseline', id],
    queryFn: () =>
      api<{ askable: number; done: boolean }[]>({ path: `/v1/profiles/${id}/baseline` }),
  });
  const openBranches = (baseline.data ?? []).filter((b) => b.askable > 0 && !b.done).length;

  const log = useMutation({
    mutationFn: (item: PlanItem) =>
      Api.logPractice(id, {
        nodeKey: item.nodeKey,
        practiceKey: item.practiceKey ?? undefined,
        title: item.practiceKey ? undefined : item.title,
        setting: item.setting,
        result: 'went_well',
        clientEventId: `web-${crypto.randomUUID()}`,
      }),
    onSuccess: (r) => {
      setToast(r.summary);
      void qc.invalidateQueries({ queryKey: ['today', id] });
      void qc.invalidateQueries({ queryKey: ['profile', id] });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const isFamily = profile.myLevel === 'owner' || profile.myLevel === 'member';
  const home = (q.data?.practices ?? []).filter((p) => p.setting === 'home' || isFamily);

  return (
    <main className="page">
      {toast && <div className="notice">{toast}</div>}
      {openBranches > 0 && (
        <Link
          to="/p/$id/baseline"
          params={{ id }}
          className="card"
          style={{ color: 'inherit', background: 'var(--honey-soft)', borderColor: '#f0d9ae' }}
        >
          <strong>نقطهٔ شروع {profile.displayName} را پیدا کنیم</strong>
          <span className="help">
            {fa(openBranches)} بخش هنوز سؤال‌های شروع را ندارد · چند سؤال ساده برای هر بخش
          </span>
        </Link>
      )}
      <section className="card hero">
        <div className="overline">امروز</div>
        <h2 style={{ fontSize: 'var(--fs-section)', fontWeight: 700 }}>
          {home.length === 0
            ? 'هنوز تمرینی برای خانه تعیین نشده'
            : `${fa(home.length)} تمرین برای ${profile.displayName}`}
        </h2>
        <div className="help">
          {home.length === 0
            ? 'از «نقشه» یک قدم را باز کنید و تمرینش را به برنامه اضافه کنید، یا منتظر درمانگر بمانید.'
            : 'هر بار که انجام شد، یک لمس. همین کافی است.'}
        </div>
      </section>

      {q.isLoading && <div className="skeleton" />}

      {home.map((item) => (
        <section key={item.id} className="card">
          <div className="overline">
            {item.nodeLabel} · {item.nodeTitle}
          </div>
          <strong style={{ fontSize: 'var(--fs-section)' }}>{item.title}</strong>
          {item.how && <p className="help">{item.how}</p>}
          <div className="row spread">
            <span className="help">
              {SETTING_FA[item.setting]}
              {item.timesPerWeek ? ` · ${fa(item.timesPerWeek)} بار در هفته` : ''} · این هفته{' '}
              {fa(item.doneThisWeek)} بار
            </span>
            <button
              type="button"
              className="btn honey"
              disabled={log.isPending}
              onClick={() => log.mutate(item)}
            >
              ✓ انجام شد
            </button>
          </div>
        </section>
      ))}

      {q.data && q.data.nextSteps.length > 0 && (
        <section className="card">
          <div className="overline">قدم بعدی</div>
          {q.data.nextSteps.map((n) => (
            <Link
              key={n.branchKey}
              to="/p/$id/path/$branch"
              params={{ id, branch: n.branchKey }}
              className="row spread"
              style={{ color: 'inherit', minBlockSize: 44 }}
            >
              <span>
                <span className="help">{n.branchTitle}</span>
                <br />
                <strong>{n.nodeTitle}</strong>
              </span>
              <span className={`chip ${n.status}`}>{STATUS_FA[n.status]}</span>
            </Link>
          ))}
        </section>
      )}

      {q.data && q.data.recentlyAchieved.length > 0 && (
        <section
          className="card"
          style={{ background: 'var(--st-achieved-bg)', borderColor: 'var(--st-achieved-ring)' }}
        >
          <div className="overline" style={{ color: 'var(--st-achieved)' }}>
            تازه یاد گرفته 🎉
          </div>
          {q.data.recentlyAchieved.map((r) => (
            <div key={r.nodeKey} className="row spread">
              <strong>{r.nodeTitle}</strong>
              <span className="help">{formatJalali(r.at)}</span>
            </div>
          ))}
        </section>
      )}

      <Link to="/p/$id/map" params={{ id }} className="btn quiet block">
        نقشهٔ کلی مسیر رشد
      </Link>
    </main>
  );
}
